/**
 * ChamadoDealDetailDialog — modal admin com detalhes do chamado Corretiva.
 *
 * BUG-B1.6.2 Phase 1+2. Substitui (pra Corretiva) o DealDetailDialog do
 * Comercial. Mantém arquitetura paralela mas reusa componentes universais
 * (CardTagsSection, CreateTaskDialog) pra paridade com Comercial.
 *
 * Conteúdo:
 *   - Header: #N + título + badge fase atual
 *   - Cliente + criado por + criado em + responsável admin
 *   - Foto anexada (presigned URL R2 sob demanda)
 *   - Descrição original do chamado
 *   - Bloco de Agendamento (técnico + data + link OS + status) quando agendada
 *   - Editor de "Relato da execução" quando concluída (admin pode editar obs OS)
 *   - Tags (CardTagsSection — integra com automações por tag)
 *   - Timeline de comentários (todos — internos + visíveis)
 *   - Form pra adicionar comentário admin
 *   - Botões: Cancelar chamado / Nova tarefa / Atribuir técnico / Fechar
 *
 * NÃO REVERTER:
 *   - CardTagsSection workflowSlug='corretiva' — automações por tag funcionam
 *   - CreateTaskDialog workflowSlug='corretiva' — tarefa pra QUALQUER user
 *     (não só técnico — colaborador admin/financeiro etc)
 *   - Foto via /api/v1/admin/chamados/:dealId/foto-url (presigned R2)
 *   - Edit OS observacoes vai pra PUT /service-orders/:id mesmo se concluida
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// FASE-C (design system): emoji-ícone banido — Lucide only.
import { Pencil, Trash2 } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@/components/ui';
import { useCardComments, useCommentActions } from '@/lib/card-timeline-store';
import { useClientNome } from '@/lib/clients-store';
import { useUsers } from '@/lib/admin-store';
import { useDealActions } from '@/lib/comercial-store';
import { useAuth } from '@/contexts/AuthContext';
import {
  faseBadgeVariant,
  faseLabel,
} from '@/lib/cliente-portal-store';
import { api } from '@/lib/api';
import type { Deal } from '@/lib/comercial-types';
import { CardTagsSection } from '@/components/cards/CardTagsSection';
import { CreateTaskDialog } from '@/components/cards/CreateTaskDialog';

import { AtribuirTecnicoDialog } from './AtribuirTecnicoDialog';
import { CommentBody } from './CommentBody';

import { formatarData } from '@/lib/format-date';
interface ChamadoDealDetailDialogProps {
  open: boolean;
  deal: Deal;
  onClose: () => void;
}

interface LinkedOsInfo {
  osId: string;
  numeroHumano: number | null;
  status: string;
  tecnicoId: string | null;
  agendadoParaIso: string | null;
  tokenPublico: string | null;
  observacoes: string | null;
}

/**
 * Hook pra buscar a OS linkada a este deal (via service_orders.deal_id).
 * Retorna null se não tem OS ainda (fase=aberta).
 */
function useLinkedOs(dealId: string) {
  return useQuery({
    queryKey: ['v1', 'service-orders', 'by-deal', dealId] as const,
    queryFn: async (): Promise<LinkedOsInfo | null> => {
      try {
        const r = await api.get<{
          items?: Array<{
            id: string;
            numeroHumano: number | null;
            status: string;
            tecnicoId: string | null;
            agendadoParaIso: string | null;
            observacoes: string | null;
          }>;
        }>(`/api/v1/service-orders?deal_id=${encodeURIComponent(dealId)}`);
        const first = r.items?.[0];
        if (!first) return null;

        // Busca o token público (se existir)
        let tokenPublico: string | null = null;
        try {
          const links = await api.get<Array<{ token: string; criadoEmIso: string }>>(
            `/api/v1/service-orders/${encodeURIComponent(first.id)}/external-links`,
          );
          tokenPublico = links?.[0]?.token ?? null;
        } catch {
          /* best-effort */
        }

        return {
          osId: first.id,
          numeroHumano: first.numeroHumano,
          status: first.status,
          tecnicoId: first.tecnicoId,
          agendadoParaIso: first.agendadoParaIso,
          tokenPublico,
          observacoes: first.observacoes,
        };
      } catch {
        return null;
      }
    },
    staleTime: 15_000,
  });
}

export function ChamadoDealDetailDialog({
  open,
  deal,
  onClose,
}: ChamadoDealDetailDialogProps) {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const clienteNome = useClientNome(deal.clienteId);
  const users = useUsers();
  const { show: showToast } = useToast();
  const { changeStage } = useDealActions();
  const qc = useQueryClient();

  const { data: comentarios = [] } = useCardComments(deal.id, 'corretiva');
  const linkedOs = useLinkedOs(deal.id);
  const commentActions = useCommentActions();

  const [atribuirOpen, setAtribuirOpen] = useState(false);
  const [novaTarefaOpen, setNovaTarefaOpen] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [novoComentario, setNovoComentario] = useState('');
  const [comentarioVisivel, setComentarioVisivel] = useState(false);
  const [relatoEdit, setRelatoEdit] = useState<string | null>(null);

  const podeAtribuir = deal.fase === 'aberta';
  const podeCancelar = deal.fase === 'aberta' || deal.fase === 'agendada';
  const isConcluida = deal.fase === 'concluida';

  const tecnicoNome = (() => {
    if (!linkedOs.data?.tecnicoId) return null;
    const t = users.find((u) => u.id === linkedOs.data!.tecnicoId);
    return t?.nome ?? linkedOs.data.tecnicoId;
  })();

  // ─── Edit relato OS (observacoes) ──────────────────────────────────
  const editRelatoMutation = useMutation({
    mutationFn: async (novoRelato: string) => {
      if (!linkedOs.data) throw new Error('os-not-linked');
      return api.put(`/api/v1/service-orders/${linkedOs.data.osId}`, {
        observacoes: novoRelato,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['v1', 'service-orders'] });
      setRelatoEdit(null);
      showToast(
        'Relato atualizado. Link público OS reflete a nova versão.',
        { variant: 'success' },
      );
    },
    onError: (err) => {
      showToast(`Erro ao salvar relato: ${(err as Error).message}`, { variant: 'error' });
    },
  });

  async function handleCancelar() {
    if (!user) return;
    if (!confirm('Cancelar este chamado? Ação reversível só por admin.')) return;
    setCancelando(true);
    try {
      await changeStage(deal.id, 'cancelada', user.id, user.name, {
        from: faseLabel(deal.fase),
        to: 'Canceladas',
      });
      showToast('Chamado cancelado.', { variant: 'success' });
      onClose();
    } catch (err) {
      showToast(`Erro: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setCancelando(false);
    }
  }

  async function handleNovoComentario(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !novoComentario.trim()) return;
    try {
      await commentActions.createComment(deal.id, 'corretiva', {
        corpo: novoComentario.trim(),
        visivelCliente: comentarioVisivel,
      });
      setNovoComentario('');
      setComentarioVisivel(false);
      showToast('Comentário adicionado.', { variant: 'success' });
    } catch (err) {
      showToast(`Erro: ${(err as Error).message}`, { variant: 'error' });
    }
  }

  function formatIso(iso: string | null | undefined): string {
    if (!iso) return '';
    try {
      return formatarData(iso, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-3 flex-wrap">
              <span>
                {deal.humanLabel ? `${deal.humanLabel} · ` : ''}
                {deal.titulo}
              </span>
              <Badge variant={faseBadgeVariant(deal.fase)} size="sm">
                {faseLabel(deal.fase)}
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-5">
            {/* Metadados */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="block text-xs uppercase text-ink-3 dark:text-ink-dark-3">
                  Cliente
                </span>
                <span className="text-ink-1 dark:text-ink-dark-1">
                  {clienteNome || '—'}
                </span>
              </div>
              <div>
                <span className="block text-xs uppercase text-ink-3 dark:text-ink-dark-3">
                  Aberto em
                </span>
                <span className="text-ink-1 dark:text-ink-dark-1">
                  {formatIso(deal.criadoEmIso)}
                </span>
              </div>
              {deal.responsavelNome && (
                <div>
                  <span className="block text-xs uppercase text-ink-3 dark:text-ink-dark-3">
                    Responsável (admin)
                  </span>
                  <span className="text-ink-1 dark:text-ink-dark-1">
                    {deal.responsavelNome}
                  </span>
                </div>
              )}
              {linkedOs.data && (
                <div>
                  <span className="block text-xs uppercase text-ink-3 dark:text-ink-dark-3">
                    OS vinculada
                  </span>
                  <span className="text-ink-1 dark:text-ink-dark-1">
                    {linkedOs.data.numeroHumano != null
                      ? `#${linkedOs.data.numeroHumano}`
                      : linkedOs.data.osId.slice(0, 12)}
                  </span>
                </div>
              )}
            </section>

            {/* Bloco de atribuição (se agendada/concluida) */}
            {linkedOs.data && (deal.fase === 'agendada' || isConcluida) && (
              <Alert variant="info">
                {/* FASE-C: emoji-ícone fora — o rótulo em negrito já orienta. */}
                <div className="space-y-1 text-sm">
                  <div>
                    <strong>Técnico:</strong> {tecnicoNome ?? '—'}
                  </div>
                  <div>
                    <strong>Agendado para:</strong>{' '}
                    {formatIso(linkedOs.data.agendadoParaIso)}
                  </div>
                  <div>
                    <strong>Status da OS:</strong> {linkedOs.data.status}
                  </div>
                  {linkedOs.data.tokenPublico && (
                    <div>
                      <strong>Link público:</strong>{' '}
                      <a
                        href={`/os/${linkedOs.data.tokenPublico}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red underline"
                      >
                        /os/{linkedOs.data.tokenPublico.slice(0, 12)}…
                      </a>
                    </div>
                  )}
                </div>
              </Alert>
            )}

            {/* Foto anexada: REMOVIDA por pedido do Matheus 2026-05-28 noite.
                Renderização inline causou aninhamento de URL + a section topo
                mostrava imagem errada em alguns casos. Pra exibir a foto do
                chamado, abrir o link público da OS (/os/:token) quando criada.
                Tech-debt: revisar UX de foto inline numa próxima rodada. */}

            {/* Descrição original */}
            {deal.descricao && (
              <section>
                <h3 className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 mb-2">
                  Descrição do chamado
                </h3>
                <p className="text-sm whitespace-pre-wrap text-ink-1 dark:text-ink-dark-1 bg-paper-warm dark:bg-paper-dark-warm border border-line dark:border-line-dark rounded p-3">
                  {deal.descricao}
                </p>
              </section>
            )}

            {/* Tags — integração com automações por tag */}
            <section>
              <CardTagsSection
                cardId={deal.id}
                cardType="deal"
                workflowSlug="corretiva"
              />
            </section>

            {/* Relato da execução (editável quando concluida) */}
            {isConcluida && linkedOs.data && (
              <section>
                <h3 className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 mb-2 flex items-center justify-between">
                  <span>Relato da execução (técnico)</span>
                  {relatoEdit === null && isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setRelatoEdit(linkedOs.data?.observacoes ?? '')
                      }
                      data-testid="relato-edit-btn"
                    >
                      <Pencil className="w-4 h-4 mr-1.5" aria-hidden="true" />
                      Editar
                    </Button>
                  )}
                </h3>
                {relatoEdit !== null ? (
                  <div className="space-y-2">
                    <textarea
                      value={relatoEdit}
                      onChange={(e) => setRelatoEdit(e.target.value)}
                      rows={6}
                      className="block w-full px-3 py-2 rounded border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface focus:outline-none focus:ring-2 focus:ring-red text-sm"
                      data-testid="relato-textarea"
                    />
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                      Atualização do relato reflete imediatamente no link público da OS
                      (cliente vê a versão nova).
                    </p>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRelatoEdit(null)}
                        disabled={editRelatoMutation.isPending}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => editRelatoMutation.mutate(relatoEdit)}
                        disabled={editRelatoMutation.isPending}
                        data-testid="relato-salvar-btn"
                      >
                        {editRelatoMutation.isPending ? 'Salvando…' : 'Salvar relato'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap text-ink-1 dark:text-ink-dark-1 bg-paper-warm dark:bg-paper-dark-warm border border-line dark:border-line-dark rounded p-3 min-h-[60px]">
                    {linkedOs.data.observacoes || (
                      <em className="text-ink-3 dark:text-ink-dark-3">
                        Sem relato registrado.
                      </em>
                    )}
                  </p>
                )}
              </section>
            )}

            {/* Timeline comentários */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 mb-2">
                Comentários ({comentarios.filter((c) => !c.deletadoEmIso).length})
              </h3>
              {comentarios.filter((c) => !c.deletadoEmIso).length === 0 ? (
                <p className="text-sm text-ink-3 dark:text-ink-dark-3">
                  Sem comentários ainda.
                </p>
              ) : (
                <div className="space-y-2">
                  {comentarios
                    .filter((c) => !c.deletadoEmIso)
                    .map((c) => {
                      const autor = users.find((u) => u.id === c.autorId);
                      const podeApagar = isAdmin || c.autorId === user?.id;
                      return (
                        <div
                          key={c.id}
                          className="border border-line dark:border-line-dark rounded p-3 bg-paper-surface dark:bg-paper-dark-surface"
                          data-testid={`admin-comentario-${c.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1 text-xs">
                            <span className="font-medium text-ink-1 dark:text-ink-dark-1">
                              {autor?.nome ?? 'Sistema'}
                            </span>
                            <span className="text-ink-3 dark:text-ink-dark-3">
                              {formatIso(c.criadoEmIso)}
                            </span>
                            {podeApagar && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!user) return;
                                  if (
                                    !confirm(
                                      'Apagar este comentário? O cliente NÃO é notificado.',
                                    )
                                  )
                                    return;
                                  try {
                                    await commentActions.deleteComment(c.id, deal.id);
                                    showToast('Comentário apagado.', { variant: 'success' });
                                  } catch (err) {
                                    showToast(
                                      `Erro ao apagar: ${(err as Error).message}`,
                                      { variant: 'error' },
                                    );
                                  }
                                }}
                                className="ml-auto text-ink-3 dark:text-ink-dark-3 hover:text-danger text-xs"
                                aria-label="Apagar comentário"
                                title="Apagar (sem notificar cliente)"
                                data-testid={`admin-comentario-${c.id}-delete`}
                              >
                                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                              </button>
                            )}
                          </div>
                          <CommentBody
                            corpo={c.corpo}
                            fotoR2Key={deal.fotoR2Key}
                          />
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Form novo comentário */}
              <form onSubmit={handleNovoComentario} className="mt-3 space-y-2">
                <textarea
                  value={novoComentario}
                  onChange={(e) => setNovoComentario(e.target.value)}
                  placeholder="Adicionar comentário…"
                  rows={2}
                  maxLength={5000}
                  className="block w-full px-3 py-2 rounded border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface focus:outline-none focus:ring-2 focus:ring-red text-sm"
                  data-testid="admin-comentario-input"
                />
                <div className="flex items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-xs text-ink-2 dark:text-ink-dark-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={comentarioVisivel}
                      onChange={(e) => setComentarioVisivel(e.target.checked)}
                    />
                    Visível ao cliente
                  </label>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={!novoComentario.trim() || commentActions.pending === 'create'}
                    data-testid="admin-comentario-submit"
                  >
                    {commentActions.pending === 'create' ? 'Enviando…' : 'Comentar'}
                  </Button>
                </div>
              </form>
            </section>
          </div>
        </DialogContent>
        <DialogFooter>
          <div className="flex items-center justify-between w-full gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {podeCancelar && isAdmin && (
                <Button
                  variant="ghost"
                  onClick={handleCancelar}
                  disabled={cancelando}
                  data-testid="chamado-cancelar"
                >
                  {cancelando ? 'Cancelando…' : 'Cancelar chamado'}
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => setNovaTarefaOpen(true)}
                data-testid="chamado-nova-tarefa"
              >
                + Nova tarefa
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={onClose}>
                Fechar
              </Button>
              {podeAtribuir && (
                <Button
                  variant="primary"
                  onClick={() => setAtribuirOpen(true)}
                  data-testid="chamado-atribuir-btn"
                >
                  Atribuir técnico
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </Dialog>

      {atribuirOpen && (
        <AtribuirTecnicoDialog
          open={atribuirOpen}
          dealId={deal.id}
          dealTitulo={deal.titulo}
          dealNumeroHumano={deal.numeroHumano ?? null}
          onClose={() => setAtribuirOpen(false)}
          onAssigned={() => onClose()}
        />
      )}

      {novaTarefaOpen && (
        <CreateTaskDialog
          open={novaTarefaOpen}
          onClose={() => setNovaTarefaOpen(false)}
          cardId={deal.id}
          workflowSlug="corretiva"
        />
      )}
    </>
  );
}
